import { useState, useEffect, useRef, CSSProperties } from "react";
import { createPortal } from "react-dom";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { DatePicker } from "./DatePicker.js";
import { AsyncButton } from "./ui/async-button.js";
import { Button } from "./ui/button.js";
import { SelectField } from "./ui/select-field.js";
import { Checkbox } from "./ui/checkbox.js";
import { Input } from "./ui/input.js";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog.js";
import { Pencil } from "lucide-react";
import type { CompSnapshotDetail } from "./CompVerificationPanel.js";

export interface BreakdownLine {
  componentKey:  string;
  annualAmount:  number;
  monthlyAmount: number;
  source:        string;
  rateUsed:      string | undefined;
  baseUsed:      string | undefined;
}

interface Props {
  open:         boolean;
  employeeId:   string;
  employeeType: string | undefined;
  dateOfBirth:  string | undefined;
  onClose:      () => void;
  onCreated:    () => void;
  /** A revision Finance rejected: the drawer opens pre-filled and resubmits it instead of creating a new one. */
  revision?:    CompSnapshotDetail | null;
  /** HRMS-0633: when the hire's offer letter has been parsed and a positive annual
   *  CTC extracted, open fresh with that value pre-filled and Reason = NEW_HIRE
   *  so HR confirms with one click instead of retyping it. Ignored when
   *  `revision` is set (resubmitting a rejected revision always wins). */
  initialCtcFromOfferLetter?: number | null;
}

const REASONS = [
  { value: "NEW_HIRE",         label: "New Hire" },
  { value: "ANNUAL_APPRAISAL", label: "Annual Appraisal" },
  { value: "PROMOTION",        label: "Promotion" },
  { value: "RESTRUCTURING",    label: "Restructuring" },
  { value: "CORRECTION",       label: "Correction" },
] as const;

type Reason = typeof REASONS[number]["value"];

// Standard-structure earnings (every employee uses the Standard structure).
const EARNINGS = ["BASIC","HRA","LTA","TEL","CEA","HOSTEL","VEHICLE","OTHER_ALLOWANCES","BONUS"];
const DEDUCTIONS = ["PF_EMPLOYEE","ESI_EMPLOYEE","MEDICAL_INSURANCE","INCOME_TAX","PROF_TAX"];
const EMPLOYER = ["PF_EMPLOYER","ESI_EMPLOYER","GRATUITY"];

const LABELS: Record<string, string> = {
  BASIC:             "Basic Salary",
  HRA:               "House Rent Allowance",
  LTA:               "Leave Travel Allowance",
  TEL:               "Telephone & Internet",
  CEA:               "Children Education",
  HOSTEL:            "Children Hostel",
  VEHICLE:           "Vehicle Maintenance",
  BONUS:             "Bonus",
  OTHER_ALLOWANCES:  "Other Allowances",
  PF_EMPLOYEE:       "PF — Employee",
  ESI_EMPLOYEE:      "ESI — Employee",
  MEDICAL_INSURANCE: "Medical Insurance",
  INCOME_TAX:        "Income Tax",
  PROF_TAX:          "Professional Tax",
  PF_EMPLOYER:       "PF — Employer",
  ESI_EMPLOYER:      "ESI — Employer",
  GRATUITY:          "Gratuity",
};

/** Statutory lines show the rate the server actually applied (from Admin > Comp Rules). */
const RATED = new Set(["PF_EMPLOYEE", "PF_EMPLOYER", "ESI_EMPLOYEE", "ESI_EMPLOYER", "GRATUITY"]);
function lineLabel(l: BreakdownLine): string {
  const base = LABELS[l.componentKey] ?? l.componentKey;
  if (!RATED.has(l.componentKey) || !l.rateUsed) return base;
  const rate = parseFloat(l.rateUsed);
  const pct = `${+(rate * 100).toFixed(2)}%`;
  return `${base} (${pct})`;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

const inp: CSSProperties = {
  padding: "8px 10px",
  border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)",
  fontSize: 13,
  width: "100%",
  background: "var(--color-surface-0)",
  outline: "none",
  boxSizing: "border-box",
  color: "var(--color-surface-900)",
  fontFamily: "inherit",
};
const label: CSSProperties = { fontSize: 11, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4, display: "block" };

export function CompRevisionDrawer({ open, employeeId, employeeType, dateOfBirth, onClose, onCreated, revision, initialCtcFromOfferLetter }: Props) {
  const [ctc, setCtc]         = useState("");
  const [bonus, setBonus]     = useState("0");
  const [regime, setRegime]   = useState<"NEW_REGIME" | "OLD_REGIME">("NEW_REGIME");
  const [pt, setPt]           = useState("0");
  const [incMed, setIncMed]   = useState(false); // unticked on page load
  const [medInsAmt, setMedInsAmt] = useState("0"); // global medical insurance ₹/month (all employees)
  const [medModalOpen, setMedModalOpen] = useState(false);
  const [medModalValue, setMedModalValue] = useState("0");
  const [medModalErr, setMedModalErr] = useState<string | null>(null);
  const [effFrom, setEffFrom] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason]   = useState<Reason>("ANNUAL_APPRAISAL");
  const [breakdownView, setBreakdownView] = useState<"monthly" | "yearly">("yearly"); // preview opens on the yearly view
  const [preview, setPreview] = useState<BreakdownLine[] | null>(null);
  const [taxByRegime, setTaxByRegime] = useState<Record<string, number> | null>(null);
  const [reconciliation, setReconciliation] = useState<{ label: string; difference: number } | null>(null);
  const [takeHome, setTakeHome] = useState<number | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset on open (pre-filled from the rejected revision when resubmitting)
  useEffect(() => {
    if (!open) return;
    if (revision) {
      setCtc(String(parseFloat(String(revision.ctcAnnual))));
      setBonus(String(parseFloat(String(revision.bonusAnnual))));
      setRegime(revision.taxRegime); setPt(String(parseFloat(String(revision.professionalTaxMonthly))));
      setIncMed(revision.includeMedicalInsurance ?? false);
      setEffFrom(revision.effectiveFrom.slice(0, 10)); setReason(revision.reason as Reason);
    } else {
      // HRMS-0633: pre-fill from the parsed offer letter when the CTC is known.
      // Bonus / regime / PT / effFrom stay at today's defaults so HR can tweak
      // them before Submit; the AI only tells us the headline annual figure.
      const hasOfferCtc = typeof initialCtcFromOfferLetter === "number" && Number.isFinite(initialCtcFromOfferLetter) && initialCtcFromOfferLetter > 0;
      setCtc(hasOfferCtc ? String(initialCtcFromOfferLetter) : "");
      setBonus("0"); setRegime("NEW_REGIME"); setPt("0"); setIncMed(false);
      setEffFrom(new Date().toISOString().slice(0, 10));
      setReason(hasOfferCtc ? "NEW_HIRE" : "ANNUAL_APPRAISAL");
    }
    // The medical-insurance premium is a single global amount for all employees.
    api.get<{ monthly: number }>("/compensation/medical-insurance")
      .then(r => setMedInsAmt(String(r.data.monthly)))
      .catch(err => logError("CompRevisionDrawer.fetchMedicalInsurance", err));
    setPreview(null); setWarnings([]); setPreviewError(null); setError(null);
  }, [open, revision, initialCtcFromOfferLetter]);

  // Debounced preview
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const ctcNum = parseFloat(ctc);
    if (!ctc || isNaN(ctcNum) || ctcNum <= 0) { setPreview(null); setWarnings([]); setPreviewError(null); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.post<{ lines: BreakdownLine[]; warnings: string[]; taxByRegime?: Record<string, number>; reconciliation?: { label: string; difference: number }; takeHomeBeforeTax?: number }>("/compensation/compute-preview", {
          ctcAnnual:               ctcNum, // Annual CTC as entered; the bonus is less from it (Fixed CTC = CTC − Bonus)
          bonusAnnual:             parseFloat(bonus) || 0,
          taxRegime:               regime,
          professionalTaxMonthly:  parseFloat(pt) || 0,
          includeMedicalInsurance: incMed,
          medicalInsuranceMonthly: parseFloat(medInsAmt) || 0,
          employeeType:            employeeType ?? null,
          dateOfBirth:             dateOfBirth ?? null,
        });
        setPreview(data.lines);
        setTaxByRegime(data.taxByRegime ?? null);
        setReconciliation(data.reconciliation ?? null);
        setTakeHome(data.takeHomeBeforeTax ?? null);
        setWarnings(data.warnings);
        setPreviewError(null);
      } catch (err) {
        logError("CompRevisionDrawer.computePreview", err);
        setPreview(null);
        setWarnings([]);
        setPreviewError(errMsg(err) ?? "Couldn't calculate the breakdown.");
      }
      setLoading(false);
    }, 600);
  }, [ctc, bonus, regime, pt, incMed, medInsAmt, employeeType, dateOfBirth]);

  async function handleSave() {
    const ctcNum = parseFloat(ctc);
    if (!ctc || isNaN(ctcNum) || ctcNum <= 0) { setError("Enter a valid CTC amount"); throw new Error("validation"); }
    if (!effFrom) { setError("Effective From date is required"); throw new Error("validation"); }
    setSaving(true); setError(null);
    try {
      const body = {
        ctcAnnual:               ctcNum, // Annual CTC as entered; Fixed CTC = CTC − Bonus (salary.txt)
        bonusAnnual:             parseFloat(bonus) || 0,
        taxRegime:               regime,
        professionalTaxMonthly:  parseFloat(pt) || 0,
        includeMedicalInsurance: incMed,
        medicalInsuranceMonthly: parseFloat(medInsAmt) || 0,
        effectiveFrom:           effFrom,
        reason,
      };
      if (revision) await api.put(`/employees/${employeeId}/compensation/snapshots/${revision.id}`, body);
      else          await api.post(`/employees/${employeeId}/compensation/snapshots`, body);
      onCreated();
    } catch (err: unknown) {
      logError("CompRevisionDrawer.handleSave", err);
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? "Failed to save. Please try again.");
      throw err;
    } finally {
      setSaving(false);
    }
  }

  // Bonus is a yearly payout, so it is excluded from the monthly Gross Pay / Take Home figures.
  const grossPay  = preview ? EARNINGS.filter(k => k !== "BONUS").reduce((s, k) => s + (preview.find(l => l.componentKey === k)?.annualAmount ?? 0), 0) : 0;
  const totalDed  = preview ? DEDUCTIONS.reduce((s, k) => s + (preview.find(l => l.componentKey === k)?.annualAmount ?? 0), 0) : 0;
  const netPay    = grossPay - totalDed;

  // One breakdown row's amount, shown monthly or yearly per the toggle. A yearly payout
  // (bonus: monthly 0) is always shown as an annual figure regardless of the toggle.
  const lineAmt = (l: BreakdownLine) =>
    (l.monthlyAmount === 0 && l.annualAmount > 0) || breakdownView === "yearly"
      ? `₹${fmt(l.annualAmount)}/yr`
      : `₹${fmt(l.monthlyAmount)}/mo`;

  // Summary tiles follow the same toggle: the annual figure, or annual ÷ 12 (bonus excluded in both).
  const yearly    = breakdownView === "yearly";
  const perPeriod = (annual: number) => (yearly ? annual : annual / 12);
  const periodSfx = yearly ? "/yr" : "/mo";
  // Bonus is paid once a year: hidden from the monthly view, shown as its own line + tile yearly.
  const bonusAmt  = preview?.find(l => l.componentKey === "BONUS")?.annualAmount ?? 0;
  const showBonus = yearly && bonusAmt > 0;

  if (!open) return null;

  // Rendered into <body>, not in place. Every form section is a GlowCard, whose
  // `isolate` creates a stacking context — in place, this drawer's z-index 201
  // would resolve inside that card rather than against the page, and the HR
  // panel (later in the DOM) painted over its right-hand half, hiding the Tax
  // Regime and Prof. Tax fields and clipping Save. Same portal approach as
  // ui/confirm-dialog.tsx.
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 200 }}
      />
      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 480,
        background: "var(--color-surface-0)", borderLeft: "1px solid var(--color-surface-200)",
        zIndex: 201, display: "flex", flexDirection: "column", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--color-surface-200)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{revision ? "Edit & Resubmit Revision" : "New Comp Revision"}</div>
            <div style={{ fontSize: 11, color: "var(--color-surface-500)", marginTop: 2 }}>Goes to Finance for verification before it takes effect</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--color-surface-400)", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
          {/* CTC */}
          <div>
            <span style={label}>Annual CTC *</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16, color: "var(--color-surface-500)", fontWeight: 700 }}>₹</span>
              <input
                style={{ ...inp, fontSize: 16, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                type="number"
                placeholder="e.g. 3000000"
                min="0"
                step="50000"
                value={ctc}
                onChange={e => setCtc(e.target.value)}
              />
            </div>
            {(parseFloat(bonus) || 0) > 0 && (parseFloat(ctc) || 0) > 0 && (
              <p className="m-0 mt-1 text-[11px] text-surface-500">
                Fixed CTC (CTC − bonus): <span className="font-semibold tabular-nums text-surface-800">₹{fmt((parseFloat(ctc) || 0) - (parseFloat(bonus) || 0))}</span> · the bonus is paid yearly
              </p>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Effective From */}
            <div>
              <span style={label}>Effective From *</span>
              <DatePicker
                value={effFrom}
                onChange={(d: string | null) => setEffFrom(d ?? "")}
                style={inp}
              />
            </div>

            {/* Tax Regime */}
            <div>
              <span style={label}>Tax Regime</span>
              <SelectField value={regime} onChange={e => setRegime(e.target.value as "NEW_REGIME" | "OLD_REGIME")}>
                <option value="NEW_REGIME">New Regime</option>
                <option value="OLD_REGIME">Old Regime</option>
              </SelectField>
            </div>

            {/* Bonus */}
            <div>
              <span style={label}>Bonus (Annual) - Less</span>
              <input style={inp} type="number" min="0" placeholder="0" value={bonus} onChange={e => setBonus(e.target.value)} />
            </div>

            {/* Professional Tax */}
            <div>
              <span style={label}>Prof. Tax /month</span>
              <input style={inp} type="number" min="0" placeholder="0" value={pt} onChange={e => setPt(e.target.value)} />
            </div>
          </div>

          {/* Medical insurance — global amount for all employees, edited via the modal.
              Unchecked on load; deducted into the CTC breakup only when ticked. */}
          <div>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox checked={incMed} onCheckedChange={v => setIncMed(v === true)} />
              <span className="text-[13px] text-surface-700">Include Medical Insurance deduction</span>
            </label>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[14px] font-semibold tabular-nums text-surface-800">₹{fmt(parseFloat(medInsAmt) || 0)}</span>
              <span className="text-[12px] text-surface-500">/month</span>
              <button
                type="button"
                onClick={() => { setMedModalValue(medInsAmt); setMedModalErr(null); setMedModalOpen(true); }}
                className="ml-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-brand-600 hover:bg-surface-100"
                aria-label="Edit medical insurance amount"
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
            </div>
            <p className="m-0 mt-1 text-[11px] text-surface-400">
              Standard amount for all employees{incMed ? " · deducted in this structure." : " · tick the box to include it."}
            </p>
          </div>

          {/* Reason */}
          <div>
            <span style={label}>Reason</span>
            <SelectField value={reason} onChange={e => setReason(e.target.value as Reason)}>
              {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </SelectField>
          </div>

          {/* Live Preview */}
          {loading && (
            <div style={{ textAlign: "center", padding: 16, fontSize: 12, color: "var(--color-surface-400)" }}>
              Computing breakdown…
            </div>
          )}

          {previewError && !loading && (
            <p className="m-0 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-3 py-2 text-[12px] text-[var(--color-error-fg)]">
              {previewError}
            </p>
          )}

          {preview && !loading && (
            <div style={{ background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-surface-200)", overflow: "hidden" }}>
              <div style={{ padding: "8px 14px", borderBottom: "1px solid var(--color-surface-200)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", letterSpacing: ".05em" }}>Breakdown Preview</span>
                <div className="flex items-center gap-1">
                  <Button type="button" size="sm" variant={breakdownView === "monthly" ? "default" : "ghost"} className="h-6 px-2 text-[11px]" onClick={() => setBreakdownView("monthly")}>Monthly</Button>
                  <Button type="button" size="sm" variant={breakdownView === "yearly" ? "default" : "ghost"} className="h-6 px-2 text-[11px]" onClick={() => setBreakdownView("yearly")}>Yearly</Button>
                </div>
              </div>

              {/* Earnings */}
              <div style={{ padding: "8px 14px 0" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-success)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>Earnings</div>
                {EARNINGS.map(k => {
                  const l = preview.find(x => x.componentKey === k);
                  if (!l || l.annualAmount === 0) return null;
                  if (k === "BONUS" && !yearly) return null; // not part of monthly salary
                  return (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px dashed var(--color-surface-100)", fontSize: 12 }}>
                      <span style={{ color: "var(--color-surface-600)" }}>{lineLabel(l)}</span>
                      <span style={{ fontVariantNumeric: "tabular-nums" }}>{lineAmt(l)}</span>
                    </div>
                  );
                })}
              </div>

              {/* Deductions */}
              <div style={{ padding: "8px 14px 0" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-warning-fg-strong)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>Deductions</div>
                {DEDUCTIONS.map(k => {
                  const l = preview.find(x => x.componentKey === k);
                  if (!l || l.annualAmount === 0) return null;
                  return (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px dashed var(--color-surface-100)", fontSize: 12 }}>
                      <span style={{ color: "var(--color-surface-600)" }}>{lineLabel(l)}</span>
                      <span style={{ fontVariantNumeric: "tabular-nums" }}>{lineAmt(l)}</span>
                    </div>
                  );
                })}
              </div>

              {/* Employer cost (info only) */}
              <div style={{ padding: "8px 14px 0" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-surface-400)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>Employer Cost (part of CTC)</div>
                {EMPLOYER.map(k => {
                  const l = preview.find(x => x.componentKey === k);
                  if (!l || l.annualAmount === 0) return null;
                  return (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px dashed var(--color-surface-100)", fontSize: 12 }}>
                      <span style={{ color: "var(--color-surface-400)" }}>{lineLabel(l)}</span>
                      <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--color-surface-400)" }}>{lineAmt(l)}</span>
                    </div>
                  );
                })}
              </div>

              {warnings.length > 0 && (
                <div className="mx-3.5 mt-3 flex flex-col gap-1 rounded-md border border-[var(--color-warning-border)] bg-[var(--color-warning-bg-subtle)] px-3 py-2 text-[12px] text-[var(--color-warning-fg)]">
                  {warnings.map(w => <p key={w} className="m-0">{w}</p>)}
                </div>
              )}

              {/* Reconciliation difference — salary.txt rule 8/9 */}
              {reconciliation && (
                <div className={`mx-3.5 mt-3 rounded-md border px-3 py-2 text-[12px] ${Math.abs(reconciliation.difference) < 1 ? "border-[var(--color-success-border)] bg-[var(--color-success-bg-subtle)] text-[var(--color-success-fg)]" : "border-[var(--color-error-border)] bg-[var(--color-error-bg)] text-[var(--color-error-fg)]"}`}>
                  {reconciliation.label}: {Math.abs(reconciliation.difference) < 1 ? "reconciled ✓" : `difference ₹${fmt(reconciliation.difference)}`}
                </div>
              )}

              {/* Income tax under both regimes — the selected one is applied */}
              {taxByRegime && (
                <div className="mx-3.5 mt-3 flex items-center justify-between gap-3 rounded-md border border-surface-200 bg-surface-0 px-3 py-2 text-[12px]">
                  {(["NEW_REGIME","OLD_REGIME"] as const).map(rk => {
                    const applied = regime === rk;
                    const lower = taxByRegime["NEW_REGIME"]! <= taxByRegime["OLD_REGIME"]! ? "NEW_REGIME" : "OLD_REGIME";
                    return (
                      <div key={rk} className={applied ? "font-semibold text-surface-900" : "text-surface-500"}>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-surface-400">
                          {rk === "NEW_REGIME" ? "New regime" : "Old regime"}{applied && " · applied"}{rk === lower && !applied && " · lower"}
                        </div>
                        ₹{fmt(perPeriod(taxByRegime[rk]!))}{periodSfx}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Net summary */}
              <div style={{ padding: "10px 14px", borderTop: "1px solid var(--color-surface-200)", display: "grid", gridTemplateColumns: showBonus ? "1fr 1fr 1fr" : "1fr 1fr", gap: 8, marginTop: 8 }}>
                <div style={{ background: "var(--color-surface-100)", borderRadius: 6, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", marginBottom: 2 }}>Gross Pay</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-success)", fontVariantNumeric: "tabular-nums" }}>₹{fmt(perPeriod(grossPay))}<span style={{ fontSize: 10, fontWeight: 400, color: "var(--color-surface-400)" }}>{periodSfx}</span></div>
                </div>
                {showBonus && (
                  <div style={{ background: "var(--color-surface-100)", borderRadius: 6, padding: "8px 10px" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", marginBottom: 2 }}>Bonus (paid yearly)</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-warning-fg-strong)", fontVariantNumeric: "tabular-nums" }}>₹{fmt(bonusAmt)}<span style={{ fontSize: 10, fontWeight: 400, color: "var(--color-surface-400)" }}>/yr</span></div>
                  </div>
                )}
                <div style={{ background: "var(--color-surface-100)", borderRadius: 6, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", marginBottom: 2 }}>Take Home (before tax)</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-brand-600)", fontVariantNumeric: "tabular-nums" }}>₹{fmt(perPeriod(takeHome ?? netPay))}<span style={{ fontSize: 10, fontWeight: 400, color: "var(--color-surface-400)" }}>{periodSfx}</span></div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div style={{ fontSize: 12, color: "var(--color-error)", padding: "8px 12px", background: "var(--color-error-bg)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-error-border)" }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid var(--color-surface-200)", display: "flex", gap: 10 }}>
          <AsyncButton
            onClick={() => handleSave()}
            confirm={{
              title: revision ? "Resubmit this revision to Finance?" : "Send this compensation revision to Finance?",
              description: "It stays unconfirmed until Finance verifies it. The current compensation stays in effect until then.",
              actionText: "Submit to Finance",
            }}
            disabled={saving}
          >
            Submit to Finance
          </AsyncButton>
          {/* The Button primitive, not a hand-styled <button>: it is what keeps
              Cancel the same height, radius and type scale as Save. */}
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>

      {/* Edit the global medical-insurance amount (changes it for all employees) */}
      <Dialog open={medModalOpen} onOpenChange={setMedModalOpen}>
        {/* The drawer sits at z-index 201; raise both the backdrop and this modal above it so it's clearly on top. */}
        <DialogContent overlayClassName="z-1000" className="z-1000 max-w-sm">
          <DialogHeader>
            <DialogTitle>Medical Insurance amount</DialogTitle>
            <DialogDescription>This is the standard premium for every employee. Changing it updates all future revisions.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-surface-500">₹</span>
            <Input type="number" min="0" step="10" value={medModalValue} onChange={e => setMedModalValue(e.target.value)} className="w-40" />
            <span className="text-[13px] text-surface-500">/month</span>
          </div>
          {medModalErr && <p className="m-0 text-[12px] text-[var(--color-error-fg)]">{medModalErr}</p>}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setMedModalOpen(false)}>Cancel</Button>
            <AsyncButton
              variant="success"
              onClick={async () => {
                const n = parseFloat(medModalValue);
                if (!Number.isFinite(n) || n < 0) { setMedModalErr("Enter a valid amount"); throw new Error("validation"); }
                try {
                  await api.put("/compensation/medical-insurance", { monthly: n });
                  setMedInsAmt(String(n));
                  setMedModalOpen(false);
                } catch (e) {
                  logError("CompRevisionDrawer.saveMedicalInsurance", e);
                  setMedModalErr(errMsg(e) ?? "Couldn't save. Try again.");
                  throw e;
                }
              }}
            >
              Save for all
            </AsyncButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>,
    document.body,
  );
}
